import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Activity, Clock, RefreshCw } from "lucide-react";
import { useApp } from "@/context/AppContext";

/* Color scale by magnitude (USGS-ish) */
const magColor = (m) => {
  if (m >= 6) return "#7f1d1d";
  if (m >= 5) return "#b91c1c";
  if (m >= 4) return "#dc2626";
  if (m >= 3) return "#f59e0b";
  return "#fbbf24";
};
const magRadius = (m) => Math.max(4, Math.min(28, (m - 1.5) * 5));

const timeAgo = (ms, lang) => {
  if (!ms) return "—";
  const d = Math.floor((Date.now() - ms) / 1000);
  const id = lang === "id";
  if (d < 60) return id ? `${d} dtk lalu` : `${d}s ago`;
  if (d < 3600) return id ? `${Math.floor(d / 60)} mnt lalu` : `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return id ? `${Math.floor(d / 3600)} jam lalu` : `${Math.floor(d / 3600)}h ago`;
  return id ? `${Math.floor(d / 86400)} hari lalu` : `${Math.floor(d / 86400)}d ago`;
};

function StatBox({ label, value, sub, tid }) {
  return (
    <div data-testid={tid} className="flat-border rounded-xl bg-white p-3 md:p-4 min-w-0">
      <div className="text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.15em] text-[#141414]/60 leading-tight">{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-1.5 break-words leading-none">{value}</div>
      {sub && <div className="text-[10px] md:text-[11px] text-[#141414]/60 mt-1.5 truncate">{sub}</div>}
    </div>
  );
}

export default function EarthquakeMap() {
  const { lang, settings } = useApp();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const t = {
    title: (settings?.[`eq_title_${lang}`]) || (lang === "id" ? "Peta Gempa Real-time" : "Live Earthquake Map"),
    subtitle:
      (settings?.[`eq_subtitle_${lang}`]) ||
      (lang === "id"
        ? `Data langsung BMKG · USGS · sejak 15 Agt 2026 · magnitudo ≥ 2 · sekitar Flores`
        : `Live BMKG · USGS data · since 15 Aug 2026 · magnitude ≥ 2 · Flores area`),
    total: lang === "id" ? "Total Gempa" : "Total Earthquakes",
    biggest: lang === "id" ? "Magnitudo Terbesar" : "Biggest Magnitude",
    last24: lang === "id" ? "24 Jam Terakhir" : "Last 24 Hours",
    last7d: lang === "id" ? "7 Hari Terakhir" : "Last 7 Days",
    latest: lang === "id" ? "Terbaru" : "Latest",
    depth: lang === "id" ? "Kedalaman" : "Depth",
    empty: lang === "id" ? "Belum ada data gempa." : "No earthquake data yet.",
    source: lang === "id" ? "Sumber: BMKG · USGS · diperbarui otomatis" : "Source: BMKG · USGS · auto-refreshed",
    refresh: lang === "id" ? "Muat ulang" : "Refresh",
    legend: lang === "id" ? "Ukuran & warna = magnitudo" : "Size & color = magnitude",
    timeline: lang === "id" ? "Timeline Harian" : "Daily Timeline",
    timelineSub: lang === "id" ? "jumlah gempa per hari sejak 15 Agt 2026" : "quakes per day since 15 Aug 2026",
    bmkgOfficial: lang === "id" ? "Resmi BMKG" : "BMKG official",
    bmkgUpdated: lang === "id" ? "diperbarui" : "updated",
    autoDetected: lang === "id" ? "terdeteksi otomatis" : "auto-detected",
  };

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/earthquakes");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000); // refresh every 2 min
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [-8.6, 121.4],
      zoom: 7,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://openstreetmap.org/copyright">OSM</a> · quakes: <a href="https://bmkg.go.id" target="_blank">BMKG</a> · <a href="https://earthquake.usgs.gov" target="_blank">USGS</a>',
      maxZoom: 12,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Redraw markers on data change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }
    const group = L.layerGroup();
    (data.events || []).forEach((e) => {
      if (e.lat == null || e.lng == null) return;
      const marker = L.circleMarker([e.lat, e.lng], {
        radius: magRadius(e.mag),
        color: magColor(e.mag),
        fillColor: magColor(e.mag),
        fillOpacity: 0.55,
        weight: 1.5,
      });
      const dateStr = e.time ? new Date(e.time).toLocaleString(lang === "id" ? "id-ID" : "en-GB") : "—";
      marker.bindPopup(
        `<div style="font-family:Space Grotesk,system-ui,sans-serif;font-size:12px;min-width:180px">
          <div style="font-weight:700;font-size:14px;color:${magColor(e.mag)}">M ${e.mag.toFixed(1)}</div>
          <div style="margin-top:2px">${(e.place || "—").replace(/</g, "&lt;")}</div>
          <div style="margin-top:6px;color:#555">${t.depth}: ${e.depth_km != null ? e.depth_km.toFixed(1) + " km" : "—"}</div>
          <div style="color:#555">${dateStr}</div>
          ${e.url ? `<a href="${e.url}" target="_blank" rel="noopener" style="display:inline-block;margin-top:6px;color:#670003;font-weight:600">USGS →</a>` : ""}
        </div>`
      );
      marker.addTo(group);
    });
    group.addTo(map);
    layerRef.current = group;
  }, [data, lang, t.depth]);

  const s = data?.stats;
  const officialTotal = s?.official_total;
  const officialUpdated = s?.official_updated;
  const daily = data?.daily || [];
  const dailyMax = daily.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  // Admin manual overrides — falls back to fetched values when null
  const ovBiggestMag = settings?.eq_biggest_mag;
  const ovBiggestPlace = settings?.eq_biggest_place;
  const ov24h = settings?.eq_last_24h_override;
  const ovLatestMag = settings?.eq_latest_mag;
  const ovLatestPlace = settings?.eq_latest_place;
  const ovLatestTimeIso = settings?.eq_latest_time_iso;

  const biggestMagVal = ovBiggestMag != null ? Number(ovBiggestMag) : s?.biggest?.mag;
  const biggestPlaceVal = ovBiggestPlace || s?.biggest?.place || "";
  const last24Val = ov24h != null ? ov24h : s?.last_24h;
  const latestMagVal = ovLatestMag != null ? Number(ovLatestMag) : s?.latest?.mag;
  const latestPlaceVal = ovLatestPlace || s?.latest?.place || "";
  const latestTimeMs = ovLatestTimeIso ? new Date(ovLatestTimeIso).getTime() : s?.latest?.time;
  const anyOverride = ovBiggestMag != null || ovBiggestPlace || ov24h != null || ovLatestMag != null || ovLatestPlace || ovLatestTimeIso;

  // Admin can hide the whole section
  if (settings && settings.eq_show_section === false) return null;

  return (
    <section className="mb-12" data-testid="earthquake-section">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 flat-border rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--maroon)] animate-pulse" />
            {lang === "id" ? "Live · BMKG · USGS" : "Live · BMKG · USGS"}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight">{t.title}</h2>
          <p className="text-xs sm:text-sm text-[#141414]/70 mt-1">{t.subtitle}</p>
          {anyOverride && (
            <div className="text-[10px] text-[#670003]/80 mt-1" data-testid="eq-manual-override-badge">
              {lang === "id" ? "Beberapa angka diperbarui manual oleh admin" : "Some values manually updated by admin"}
            </div>
          )}
        </div>
        <button
          onClick={load}
          data-testid="eq-refresh-btn"
          disabled={loading}
          className="flat-btn bg-white rounded-full px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} /> {t.refresh}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatBox
          tid="eq-stat-total"
          label={t.total}
          value={s ? (officialTotal ?? s.total).toLocaleString("id-ID") : "—"}
          sub={
            officialTotal != null
              ? `${t.bmkgOfficial}${officialUpdated ? ` · ${t.bmkgUpdated} ${officialUpdated}` : ""}`
              : (s ? `${t.autoDetected} · ≥ M${s.min_mag} · ${s.since}` : "")
          }
        />
        <StatBox
          tid="eq-stat-biggest"
          label={t.biggest}
          value={biggestMagVal != null ? `M ${Number(biggestMagVal).toFixed(1)}` : "—"}
          sub={biggestPlaceVal || ""}
        />
        <StatBox tid="eq-stat-24h" label={t.last24} value={last24Val != null ? last24Val : "—"} sub={t.last7d + `: ${s ? s.last_7d : "—"}`} />
        <StatBox
          tid="eq-stat-latest"
          label={t.latest}
          value={latestMagVal != null ? `M ${Number(latestMagVal).toFixed(1)}` : "—"}
          sub={latestTimeMs ? (latestPlaceVal ? `${latestPlaceVal} · ${timeAgo(latestTimeMs, lang)}` : timeAgo(latestTimeMs, lang)) : latestPlaceVal}
        />
      </div>

      <div className="flat-border rounded-2xl bg-white overflow-hidden" data-testid="eq-map-wrap">
        <div ref={mapEl} className="w-full h-[320px] sm:h-[400px] md:h-[460px]" data-testid="eq-map" />
        <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 border-t border-[#141414]/10 bg-[#fff0d2]/60 text-[10px] md:text-[11px] text-[#141414]/70">
          <div className="flex items-center gap-2">
            <Activity className="w-3 h-3" strokeWidth={2} />
            <span>{t.legend}</span>
            <span className="inline-flex items-center gap-1 ml-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#fbbf24" }} /> 2-3
              <span className="w-2.5 h-2.5 rounded-full ml-1" style={{ background: "#f59e0b" }} /> 3-4
              <span className="w-2.5 h-2.5 rounded-full ml-1" style={{ background: "#dc2626" }} /> 4-5
              <span className="w-2.5 h-2.5 rounded-full ml-1" style={{ background: "#b91c1c" }} /> 5-6
              <span className="w-2.5 h-2.5 rounded-full ml-1" style={{ background: "#7f1d1d" }} /> 6+
            </span>
          </div>
          <div className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={2} /> {t.source}</div>
        </div>
      </div>

      {/* Daily timeline bar chart */}
      <div className="mt-4 flat-border rounded-2xl bg-white p-4 md:p-5" data-testid="eq-timeline">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="min-w-0">
            <div className="font-display text-lg md:text-xl leading-tight">{t.timeline}</div>
            <div className="text-[11px] text-[#141414]/60">{t.timelineSub}</div>
          </div>
          <div className="text-[10px] font-mono text-[#141414]/60">
            max/hari: <span className="font-bold text-[#141414]">{dailyMax}</span>
          </div>
        </div>
        <div className="flex items-stretch gap-1 sm:gap-1.5 h-28 md:h-32 overflow-x-auto pb-1">
          {daily.length === 0 && <div className="text-xs text-[#141414]/50 self-center w-full text-center">—</div>}
          {daily.map((d) => {
            const h = Math.max(2, (d.count / dailyMax) * 100);
            const mm = d.date.slice(5); // MM-DD
            const label = new Date(d.date + "T00:00:00").toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", { day: "2-digit", month: "short" });
            const barColor = d.max_mag >= 6 ? "#7f1d1d" : d.max_mag >= 5 ? "#b91c1c" : d.max_mag >= 4 ? "#dc2626" : d.max_mag >= 3 ? "#f59e0b" : "#fbbf24";
            return (
              <div key={d.date} className="flex flex-col items-center flex-1 min-w-[26px] h-full group relative">
                <div className="flex-1 w-full flex items-end min-h-0">
                  <div
                    data-testid={`eq-day-${d.date}`}
                    className="w-full rounded-t-md transition-colors"
                    style={{ height: `${h}%`, minHeight: d.count > 0 ? "6px" : "3px", backgroundColor: d.count === 0 ? "#e5e7eb" : barColor }}
                    title={`${label} · ${d.count} gempa · max M${d.max_mag}`}
                  />
                </div>
                <div className="text-[9px] font-mono text-[#141414]/70 mt-1 leading-none whitespace-nowrap">{mm}</div>
                <div className="text-[10px] font-bold leading-none mt-0.5">{d.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {err && <div className="mt-2 text-xs text-[color:var(--maroon)]">{err}</div>}
      {!loading && data && (data.events || []).length === 0 && (
        <div className="mt-2 text-xs text-[#141414]/60">{t.empty}</div>
      )}
    </section>
  );
}
